import type { OrderFragment } from '#graphql-operations'
import type { Ref } from 'vue'
import type { CouponPrizeStatusValue, UrlPrize } from '~/composables/useUrlCoupon'
import { couponWriteLock, previewCoupon } from '~/composables/useUrlCoupon'

/**
 * Scratch to win: putting a personal prize code on the cart.
 *
 * The code itself discounts nothing — it is the key that lets a gift-with-order
 * rule apply to this customer — so it is applied as soon as the cart can
 * actually receive the gift, and never earlier: an empty or digital-only order
 * would only hold the one-code slot and leave a join row behind. An unmet deal
 * costs the customer nothing either, because the backend detaches the code at
 * placement and it stays valid for their next order.
 *
 * Only ONE coupon may be on an order. A prize therefore never silently removes
 * a code the customer already has (it may be worth more, and `couponCodes`
 * drives affiliate and ABRA attribution): the conflict is raised, the cart
 * drawer's banner names the blocking code, and the swap happens on a tap.
 *
 * Deliberately plugin-safe: no `useI18n()` (resolved through `$i18n` instead),
 * so the coupon plugin can drive it before any component exists.
 */

export interface PrizeConflict {
  /** `<order>|<prize>|<codes on the order>` — the exact situation already answered. */
  key: string
  orderCode: string
  prizeCode: string
  blockingCodes: string[]
}

export type PrizeNotice = CouponPrizeStatusValue | 'INVALID' | 'WAITING' | 'NOT_YOURS'

const NOTICE_KEYS: Record<Exclude<PrizeNotice, 'LIVE'>, string> = {
  EXPIRED: 'scratch.prize.toast_expired',
  REDEEMED: 'scratch.prize.toast_redeemed',
  UNAVAILABLE: 'scratch.prize.toast_unavailable',
  INVALID: 'scratch.prize.toast_unavailable',
  NOT_YOURS: 'validation.gift.prize-code-not-yours',
  WAITING: 'scratch.prize.toast_waiting',
}

/**
 * The `<order>|<prize>|<codes>` situation whose apply failed for a reason that
 * says nothing about the code (a dropped connection, a refused transition), so
 * an unchanged cart does not retry it on every keystroke. Module scope: one
 * page life.
 */
let lastFailedKey: string | null = null
/** An apply that raced another write and needs one more pass against the fresh order. */
let rerun = false

export function useUrlPrize(): {
  prize: Ref<UrlPrize | null>
  conflict: Ref<PrizeConflict | null>
  swapping: Ref<boolean>
  swapError: Ref<string>
  tryApply: (order: OrderFragment | null | undefined) => Promise<'skip' | 'handled'>
  swapToPrize: () => Promise<boolean>
  announce: (notice: PrizeNotice) => void
} {
  const { $i18n } = useNuxtApp()
  const t = (key: string, named?: Record<string, unknown>) =>
    ($i18n as { t: (key: string, named?: Record<string, unknown>) => string }).t(key, named ?? {})

  const route = useRoute()
  const { prize, coupon, forget, forgetPrize } = useUrlCoupon()
  const activeOrderStore = useActiveOrderStore()
  const { syncLegacyActiveOrder } = useCartAdd()
  const { isDigitalOnlyOrder } = useDigitalCouponOrder()
  const drawers = useDrawers()
  const giftDialog = useGiftUnlockDialog()
  const auth = useAuth()
  const { toast } = useToast()
  const { lead } = useAnalytics()

  const conflict = useState<PrizeConflict | null>('url-prize-conflict', () => null)
  const swapping = useState<boolean>('url-prize-swapping', () => false)
  const swapError = useState<string>('url-prize-swap-error', () => '')

  /** A one-line answer for something that happened away from the cart drawer. */
  function announce(notice: PrizeNotice): void {
    if (notice === 'LIVE')
      return
    toast({
      variant: notice === 'WAITING' ? 'default' : 'destructive',
      title: t(NOTICE_KEYS[notice]),
    })
  }

  /**
   * Makes room and applies. An expired code refuses to go back on, which is
   * fine — it was discounting nothing — but a customer must never be left with
   * neither, so whatever was taken off is put back on a failure.
   */
  async function applyReplacing(codes: readonly string[], p: UrlPrize): Promise<void> {
    for (const code of codes)
      await activeOrderStore.removeCouponCode(code)
    try {
      await activeOrderStore.applyCouponCode(p.code)
    }
    catch (error) {
      for (const code of codes)
        await activeOrderStore.applyCouponCode(code).catch(() => {})
      syncLegacyActiveOrder()
      throw error
    }
    syncLegacyActiveOrder()
  }

  /**
   * The code is on the order — now say where the gift stands: a size to pick
   * (the gift dialog), a line already added, or a condition still to meet (the
   * `requiresCode` progress row explains that one, so nothing is opened).
   */
  function afterApplied(p: UrlPrize, via: 'auto' | 'swap'): void {
    const order = activeOrderStore.activeOrder.data
    const rule = p.ruleId
    const pending = order?.pendingGifts?.some(gift => String(gift.ruleId) === rule && gift.remaining > 0)
    const added = order?.lines?.some(line => String(line.customFields?.giftRuleId ?? '') === rule)

    lead.scratchPrizeApplied({
      cardId: rule,
      giftState: pending ? 'pending' : added ? 'added' : 'waiting_condition',
      via,
    })

    if (!pending && !added)
      return
    // Checkout opens the dialog for a new claim itself.
    if (route.path.includes('/checkout'))
      return
    if (document.getElementById('cart-drawer-panel')) {
      drawers.value.cart.open = true
      return
    }
    // A page rendered without the default layout has no drawer to open.
    if (pending)
      giftDialog.open(rule)
  }

  async function onApplyError(error: unknown, p: UrlPrize, key: string): Promise<void> {
    console.error('Failed to apply the prize code', error)
    const code = (error as { code?: string } | null)?.code

    if (code === 'COUPON_CODE_EXPIRED_ERROR') {
      forgetPrize()
      announce('EXPIRED')
      return
    }

    if (code === 'COUPON_CODE_INVALID_ERROR') {
      // A personal code only works for the address it was sent to, so a known
      // buyer who is not its owner is told exactly that; an anonymous cart
      // cannot be told apart from a withdrawn prize.
      const known = activeOrderStore.activeOrder.data?.customer || auth?.activeCustomer.value
      forgetPrize()
      announce(known ? 'NOT_YOURS' : 'UNAVAILABLE')
      return
    }

    if (code === 'COUPON_CODE_LIMIT_ERROR') {
      // Two causes the payload cannot tell apart: the one-code-per-order
      // override, and a spent usage limit. The refreshed order can.
      const state = await activeOrderStore.refetchActiveOrder().catch(() => null)
      if (state?.status === 'success')
        syncLegacyActiveOrder()
      const codes = activeOrderStore.activeOrder.data?.couponCodes ?? []
      if (codes.some(existing => existing.toUpperCase() !== p.code)) {
        rerun = true
        return
      }
      forgetPrize()
      announce('REDEEMED')
      return
    }

    // Transport, a refused transition: nothing is known about the code, so the
    // next order change retries.
    lastFailedKey = key
  }

  /**
   * `'handled'` means the prize has taken responsibility for this order state —
   * the campaign-coupon watcher must not also write to it.
   */
  async function tryApply(order: OrderFragment | null | undefined): Promise<'skip' | 'handled'> {
    const p = prize.value
    if (!p || !order?.lines?.length || order.state !== 'AddingItems')
      return 'skip'

    const existing = (order.couponCodes ?? []).map(code => code.toUpperCase())
    if (existing.includes(p.code)) {
      conflict.value = null
      return 'skip'
    }
    // The backend never gifts a digital-only order, so applying would only hold
    // the one-code slot.
    if (isDigitalOnlyOrder.value)
      return 'handled'
    if (couponWriteLock.busy)
      return 'handled'

    const key = `${order.code}|${p.code}|${existing.join(',')}`
    if (key === lastFailedKey || conflict.value?.key === key)
      return 'handled'

    couponWriteLock.busy = true
    try {
      if (existing.length) {
        const previews = await Promise.all(existing.map(code => previewCoupon(code)))
        const live = previews.filter(preview => preview.valid).map(preview => preview.code.toUpperCase())
        if (live.length) {
          conflict.value = { key, orderCode: order.code, prizeCode: p.code, blockingCodes: live }
          return 'handled'
        }
      }
      conflict.value = null
      await applyReplacing(existing, p)
      afterApplied(p, 'auto')
    }
    catch (error) {
      await onApplyError(error, p, key)
    }
    finally {
      couponWriteLock.busy = false
    }

    if (rerun) {
      rerun = false
      return tryApply(activeOrderStore.activeOrder.data)
    }
    return 'handled'
  }

  /** "Use my gift code instead": the customer chose the gift over the code they had. */
  async function swapToPrize(): Promise<boolean> {
    const p = prize.value
    const current = conflict.value
    const order = activeOrderStore.activeOrder.data
    if (!p || !current || !order || order.code !== current.orderCode || couponWriteLock.busy || swapping.value)
      return false

    swapping.value = true
    swapError.value = ''
    couponWriteLock.busy = true
    try {
      await applyReplacing(current.blockingCodes, p)
      // The campaign code is gone by choice: stop every product surface
      // advertising a price this cart will no longer give.
      if (coupon.value && current.blockingCodes.includes(coupon.value.code))
        forget()
      conflict.value = null
      afterApplied(p, 'swap')
      return true
    }
    catch (error) {
      console.error('Failed to swap to the prize code', error)
      const state = await activeOrderStore.refetchActiveOrder().catch(() => null)
      if (state?.status === 'success')
        syncLegacyActiveOrder()

      const code = (error as { code?: string } | null)?.code
      if (code === 'COUPON_CODE_EXPIRED_ERROR') {
        forgetPrize()
        swapError.value = t('scratch.prize.toast_expired')
      }
      else if (code === 'COUPON_CODE_INVALID_ERROR') {
        const known = state?.data?.customer || auth?.activeCustomer.value
        forgetPrize()
        swapError.value = t(known ? 'validation.gift.prize-code-not-yours' : 'scratch.prize.toast_unavailable')
      }
      else if (code === 'COUPON_CODE_LIMIT_ERROR'
        && !(state?.data?.couponCodes ?? []).some(existing => existing.toUpperCase() !== p.code)) {
        forgetPrize()
        swapError.value = t('scratch.prize.toast_redeemed')
      }
      else {
        swapError.value = t('scratch.prize.swap_failed')
      }
      return false
    }
    finally {
      couponWriteLock.busy = false
      swapping.value = false
    }
  }

  return { prize, conflict, swapping, swapError, tryApply, swapToPrize, announce }
}
